import type { FieldErrors, FieldError, FieldValues } from 'react-hook-form';

type ErrorLocation = {
  tab: string;
  row?: number;
  column?: number;
  label: string;
};

const DEFAULT_MESSAGE = 'Vui lòng kiểm tra lại các trường thông tin không hợp lệ.';

const headerLabels: Record<string, ErrorLocation> = {
  ma_kh: { tab: 'Thông tin', label: 'Mã nhà cung cấp' },
  so_ct: { tab: 'Thông tin', label: 'Số chứng từ' },
  ngay_ct: { tab: 'Thông tin', label: 'Ngày chứng từ' },
  so_ct0: { tab: 'Thông tin', label: 'Số hóa đơn' },
  ngay_ct0: { tab: 'Thông tin', label: 'Ngày hóa đơn' },
  ma_nt: { tab: 'Thông tin', label: 'Ngoại tệ' },
  ty_gia: { tab: 'Thông tin', label: 'Tỷ giá' },
  tk: { tab: 'Thông tin', label: 'Tài khoản có' }
};

const detailColumns: Record<string, { column: number; label: string }> = {
  ma_vt: { column: 1, label: 'Mã sản phẩm' },
  ten_vt: { column: 2, label: 'Tên sản phẩm' },
  dvt: { column: 3, label: 'Đơn vị tính' },
  ma_kho: { column: 4, label: 'Mã kho' },
  ma_vi_tri: { column: 5, label: 'Mã vị trí' },
  so_luong: { column: 6, label: 'Số lượng' },
  gia_nt0: { column: 7, label: 'Giá VND' },
  tien_nt0: { column: 8, label: 'Tiền hàng VND' },
  cp_nt: { column: 9, label: 'Chi phí VND' },
  gia_nt: { column: 10, label: 'Giá nhập VND' },
  ma_thue: { column: 11, label: 'Thuế suất' },
  thue_nt: { column: 12, label: 'Thuế GTGT VND' },
  tk_vt: { column: 13, label: 'Tk nợ' },
  tk_cpxt: { column: 13, label: 'Tk cp xuất thuế' },
  ma_bp: { column: 14, label: 'Bộ phận' },
  ma_vv: { column: 15, label: 'Vụ việc' },
  ma_hd: { column: 16, label: 'Hợp đồng' },
  ma_dtt: { column: 17, label: 'Đợt thanh toán' },
  ma_ku: { column: 18, label: 'Khuyến mãi' },
  ma_phi: { column: 19, label: 'Phí' },
  ma_cp0: { column: 20, label: 'C/p không hợp lệ' }
};

const getFieldError = (value: unknown): FieldError | null => {
  if (!value || typeof value !== 'object') return null;

  const maybeError = value as FieldError;
  return typeof maybeError.message === 'string' ? maybeError : null;
};

const humanizeFieldName = (fieldName: string) =>
  fieldName
    .replace(/_data$/, '')
    .replace(/^ma_/, 'mã ')
    .replace(/^tk_/, 'tài khoản ')
    .replace(/_/g, ' ');

const findFirstError = (value: unknown): { path: Array<string | number>; error: FieldError } | null => {
  const directError = getFieldError(value);
  if (directError) {
    return { path: [], error: directError };
  }

  if (!value || typeof value !== 'object') {
    return null;
  }

  const entries = Array.isArray(value)
    ? value.map((item, index) => [index, item] as const)
    : Object.entries(value as Record<string, unknown>);

  for (const [key, childValue] of entries) {
    const childError = findFirstError(childValue);
    if (childError) {
      return {
        path: [key, ...childError.path],
        error: childError.error
      };
    }
  }

  return null;
};

const formatLocation = (location: ErrorLocation, message?: string) => {
  const prefix =
    location.row && location.column
      ? `Tab ${location.tab}, dòng ${location.row}, cột ${location.column} - ${location.label}`
      : location.row
        ? `Tab ${location.tab}, dòng ${location.row} - ${location.label}`
        : `Tab ${location.tab}, ${location.label}`;

  return message ? `${prefix}: ${message}` : `${prefix} không hợp lệ.`;
};

export const getHoaDonMuaHangTrongNuocInvalidMessage = <TFormData extends FieldValues>(
  errors: FieldErrors<TFormData>
) => {
  const detailErrors = (errors as Record<string, any>).chi_tiet_hoa_don;

  if (Array.isArray(detailErrors)) {
    for (let rowIndex = 0; rowIndex < detailErrors.length; rowIndex += 1) {
      const rowErrors = detailErrors[rowIndex];
      if (!rowErrors || typeof rowErrors !== 'object') continue;

      for (const [field, column] of Object.entries(detailColumns)) {
        const error = getFieldError((rowErrors as Record<string, unknown>)[field]);
        if (error) {
          return formatLocation(
            {
              tab: 'Chi tiết',
              row: rowIndex + 1,
              column: column.column,
              label: column.label
            },
            error.message
          );
        }
      }
    }
  }

  for (const [field, location] of Object.entries(headerLabels)) {
    const error = getFieldError((errors as Record<string, unknown>)[field]);
    if (error) {
      return formatLocation(location, error.message);
    }
  }

  const fallbackError = findFirstError(errors);
  if (!fallbackError) {
    return DEFAULT_MESSAGE;
  }

  const [fieldName, rowIndex, detailFieldName] = fallbackError.path;
  if (fieldName === 'chi_tiet_hoa_don' && typeof rowIndex === 'number' && typeof detailFieldName === 'string') {
    const column = detailColumns[detailFieldName];

    return formatLocation(
      {
        tab: 'Chi tiết',
        row: rowIndex + 1,
        column: column?.column,
        label: column?.label || humanizeFieldName(detailFieldName)
      },
      fallbackError.error.message
    );
  }

  if (typeof fieldName === 'string') {
    return formatLocation(
      {
        tab: 'Thông tin',
        label: headerLabels[fieldName]?.label || humanizeFieldName(fieldName)
      },
      fallbackError.error.message
    );
  }

  return fallbackError.error.message || DEFAULT_MESSAGE;
};
